import { useState } from "react";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage, fmtDateTime } from "@/lib/api";

const SECTIONS = [["buyer_types", "Buyer types"], ["buying_triggers", "Buying triggers"], ["tender_terms", "Tender search terms"],
  ["web_buyer_queries", "Web buyer-signal searches"], ["partner_queries", "Partner searches"], ["suggested_exclusions", "Suggested exclusions (advisory — add to negative criteria if you agree)"]];

export function SearchPlanPanel({ campaign, onChanged }) {
  const { api, can } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const plan = campaign.search_plan;
  const place = campaign.city || campaign.region || (campaign.country === "Ireland" ? "Ireland" : campaign.country === "United Kingdom" ? "UK" : "UK / Ireland");
  const stale = plan && campaign.updated_at && new Date(campaign.updated_at) > new Date(plan.generated_at);
  const generate = async (regenerate) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/research-campaigns/${campaign.id}/search-plan`, null, { params: { regenerate } });
      toast.success(data.reused ? "Plan is up to date (profile and campaign unchanged)" : "AI search plan generated");
      onChanged();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel min-w-0 space-y-4 p-6" data-testid="search-plan-panel">
      <div className="flex flex-wrap items-center gap-2">
        <p className="eyebrow flex-1"><Sparkles className="mr-1 inline h-3.5 w-3.5 text-primary" />AI search plan{stale && <span className="ml-2 normal-case text-amber-500" data-testid="search-plan-stale">· campaign edited since — will refresh on next run</span>}</p>
        {can("write") && <Button size="sm" variant="outline" disabled={busy} onClick={() => generate(Boolean(plan))} data-testid="search-plan-generate">
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : plan ? <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}{plan ? "Regenerate" : "Preview plan"}</Button>}
      </div>
      {!plan && <p className="text-sm text-muted-foreground">Claude analyses the product profile and this campaign, then decides which tender terms and web searches to run. A plan is created automatically on the first run and reused until the profile or campaign changes.</p>}
      {plan && (
        <>
          <p className="text-sm leading-relaxed" data-testid="search-plan-analysis">{plan.analysis}</p>
          {SECTIONS.map(([k, label]) => (plan[k]?.length ? (
            <div key={k} data-testid={`search-plan-${k}`}><p className="eyebrow mb-1.5">{label}</p>
              <div className="flex flex-wrap gap-1.5">{plan[k].map((x, i) => <Pill key={i} tone={k === "suggested_exclusions" ? "amber" : "slate"}>{x.replace("{location}", place)}</Pill>)}</div></div>
          ) : null))}
          <p className="text-[11px] text-muted-foreground">Generated {fmtDateTime(plan.generated_at)}. The plan only decides where to look; every finding still needs retrieved evidence.</p>
        </>
      )}
    </div>
  );
}
